/* eslint-disable */

import React from 'react';
import styles from './Cart.module.css';
import { CardImage } from '../ui/CardImage/CardImage';
import { Card, CardLocale } from '@/src/types/card';

export const CartContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cartContainer}>{children}</div>
);

export const CardWrapper = ({ children, elevation: _elevation = 0 }: { children: React.ReactNode; elevation?: number }) => (
  <div className={styles.cardWrapper}>{children}</div>
);

export const EmptyCartMessage = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.emptyCartMessage}>{children}</div>
);

export const EmptyCartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={styles.emptyCartIcon}>
    <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2m-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2m6 14H6V8h2v2h2V8h4v2h2V8h2z"/>
  </svg>
);

export const MessageText = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.messageText}>{children}</p>
);

export const ActionButton = ({ children, variant = 'contained', ...props }: any) => (
  <button
    className={`${styles.actionButton} ${variant === 'outline' ? styles.actionButtonOutline : ''}`}
    {...props}
  >
    {children}
  </button>
);

export const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className={styles.sectionTitle}>{children}</h2>
);

export const SectionIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--color-primary)', marginRight: 12 }}>
    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2"/>
  </svg>
);

export const CartItem = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cartItem}>{children}</div>
);

interface ItemImageProps {
  card: Card | null | undefined;
  locale: CardLocale;
  discount?: number;
  onClick?: (e: React.MouseEvent) => void;
}

export const ItemImage = ({ card, locale, discount, onClick }: ItemImageProps) => {
  const hasDiscount = typeof discount === 'number' && discount > 0;
  return (
    <CardImage card={card} locale={locale} size="cart" onClick={onClick}>
      {hasDiscount && (
        <div className={styles.itemDiscountRibbon} aria-label={`${discount}% off`}>
          <span className={styles.itemDiscountRibbonStrip}>−{discount}%</span>
        </div>
      )}
    </CardImage>
  );
};

export const ItemInfo = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.itemInfo}>{children}</div>
);

export const ItemTitle = ({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => (
  <h3 className={styles.itemTitle} {...props}>{children}</h3>
);

export const CountryIndicator = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.countryIndicator}>{children}</p>
);

export const ItemPrice = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.itemPrice}>{children}</div>
);

export const OriginalPrice = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.originalPrice}>{children}</span>
);

export const DiscountedPrice = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.discountedPrice}>{children}</span>
);

export const QuantityControl = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.quantityControl}>{children}</div>
);

export const QuantityButton = ({ disabled, children, ...props }: any) => (
  <button className={styles.quantityButton} disabled={disabled} {...props}>{children}</button>
);

export const QuantityInput = (props: any) => (
  <input className={styles.quantityInput} {...props} />
);

export const RemoveButton = ({ children, ...props }: any) => (
  <button className={styles.removeButton} {...props}>{children}</button>
);

export const CartSummary = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cartSummary}>{children}</div>
);

export const SummaryRow = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.summaryRow}>{children}</div>
);

export const SubtotalLabel = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.subtotalLabel}>{children}</span>
);

export const TotalRow = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.totalRow}>{children}</div>
);

export const EmailSection = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.emailSection}>{children}</div>
);

export const RequiredIndicator = () => (
  <span className={styles.requiredIndicator}>*</span>
);

export const EmailLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className={styles.emailLabel}>
    {children}
    {required && <RequiredIndicator />}
  </label>
);

export const EmailInput = (props: any) => (
  <input className={styles.emailInput} {...props} />
);

export const GiftMessageInput = (props: any) => (
  <textarea className={styles.giftMessageInput} rows={3} {...props} />
);

export const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.errorMessage}>{children}</div>
);

export const EmailSavedAlert = ({ children, ...props }: any) => (
  <div className={styles.emailSavedAlert} {...props}>{children}</div>
);

export const CheckboxContainer = React.forwardRef<HTMLLabelElement, { children: React.ReactNode; highlight?: boolean; [key: string]: any }>(
  ({ children, highlight, ...props }, ref) => (
    <label
      ref={ref}
      className={`${styles.checkboxContainer} ${highlight ? styles.checkboxContainerHighlight : ''}`}
      {...props}
    >
      {children}
    </label>
  )
);

export const Checkbox = (props: any) => (
  <input className={styles.checkbox} {...props} />
);

export const CheckboxLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <span className={styles.checkboxLabel}>
    {children}
    {required && <RequiredIndicator />}
  </span>
);

export const CheckoutButton = ({ disabled, children, ...props }: any) => (
  <button className={styles.checkoutButton} disabled={disabled} {...props}>{children}</button>
);

export const LoadingSpinner = ({ size = 20 }: { size?: number }) => (
  <span className={styles.loadingSpinner} style={{ width: size, height: size }} />
);
